<template>
  <v-app id="app" class="app" dark v-show="!platform.prerenderer && isReady">
    <transition><div class="global-bg left" v-if="hasBackground" /></transition>
    <transition><div class="global-bg right" v-if="hasBackground" /></transition>

    <v-main>
      <transition mode="out-in">
        <router-view  v-if="isReady" />
      </transition>
    </v-main>

    <v-dialog v-model="showAuthError"  max-width="500">
      <v-card>
        <v-app-bar flat dense light>
          <v-toolbar-title v-translate>Error</v-toolbar-title>
        </v-app-bar>
        <v-divider />
        <v-card-text class="text--primary">
          <v-container class="fill-height" >
            <v-row class="fill-height" align="center" justify="center"  >
              <v-col cols="12" sm="3" class="text-center"><v-icon :size="80" color="error">alert-circle</v-icon></v-col>
              <v-col cols="12" sm="9" :style="{maxHeight: '150px', overflowY:'auto'}">{{$root.auth.authError}}</v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn light depressed rounded class="px-16 black--text" @click="showAuthError = false"><span v-translate>OK</span></v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <VersionInfo
      v-if="$root.process.env.SERVICE_STAGE !== 'master'"
      :version="$root.process.env.GIT_VERSION"
      :commit-hash="$root.process.env.GIT_COMMIT_HASH"
      />
  </v-app>
</template>

<script>
import { VersionInfo } from '@monogrid/vue-lib'
import { Platform } from '@monogrid/js-utils'
import shareImage from '@/assets/img/share.jpg'
import GetUser from './graphql/user-properties/GetUser.gql'
import GetSiteSettings from './graphql/GetSiteSettings.gql'
let initializedModernizr = false

export default {
  name: 'App',

  components: {
    VersionInfo
  },
  apollo: {
    SiteSettings: {
      query: GetSiteSettings,
      variables () {
        return {
          slug: this.$root.queryStrings.settings || 'en',
          status: 'PUBLISHED'
        }
      }
    },
    User: {
      query: GetUser,
      variables () {
        return {
          id: this.$root.auth.loggedUser.username,
          status: 'DRAFT'
        }
      },
      skip () {
        return !this.$root.auth.loggedUser
      },
      error (error, vm, key, type, options) {
        console.error(error)
        this.$root.auth.signOut()
      },
      result (data) {
        this.$root.databaseUser = this.User
      }
    }
  },

  computed: {
    hasBackground () {
      return !this.$root.userIsComplete || (!this.$route.meta || (this.$route.meta && this.$route.meta.noBackground !== true))
    }
  },

  data () {
    return {
      showAuthError: false,
      // this flag is used to show the app once the preloading
      // (if present) is finished
      isReady: false,
      // platform, accessible with an import statement
      // or by using this.$root.platform
      // NOTE: import statement is preferred
      platform: Platform
    }
  },

  mounted () {
    this.$vuetify.theme.dark = true
    if (this.$root.auth.authError) {
      this.showAuthError = true
    }
  },
  updated () {
    // if not prerenderer
    if (!Platform.prerenderer && window.Modernizr && !initializedModernizr) {
      // add custom modernizr tests
      initializedModernizr = true
      // window.Modernizr.addAsyncTest('avif', this.testAvif)
      for (const key in Platform) {
        window.Modernizr.addTest(key, () => {
          return Platform[key]
        })
      }
    }
  },
  watch: {
    SiteSettings (value) {
      if (value) {
        this.$root.siteSettings = value
        this.isReady = true
      }
    },
    '$root.siteSettings' (value) {
      if (!value) {
        this.$root.siteSettings = this.SiteSettings // trying to fix bug where sometimes root SiteSettings disappear ?
      }
    },
    showAuthError (value) {
      if (!value) this.$root.auth.authError = null
    },
    '$root.auth.authError' (value) {
      this.showAuthError = !!value
    },
    async '$root.auth.loggedUser' (value) {
      if (!value) {
        this.User = null
        this.$router.push({ name: 'Onboarding', params: { language: this.$language.current } })
      } else {
        if (this.$route.meta.onlyUnloggedUsers) {
          await this.$nextTick()
          this.$router.push({ name: 'Home', params: { language: this.$language.current } })
        }
      }
    },
    isReady (value) {
      // hide / show preloader
      this.$root.preloader.visible = !value
    }
  },

  metaInfo () {
    return {
      // if no subcomponents specify a metaInfo.title, this title will be used
      title: ' ',
      // all titles will be injected into this template
      titleTemplate: '%s | IF!Live',
      base: { href: '/' },
      meta: [
        { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
        { name: 'apple-mobile-web-app-capable', content: 'yes' },
        { name: 'mobile-web-app-capable', content: 'yes' },
        // twitter
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: '@monogridstudio' },
        { name: 'twitter:creator', content: '@monogridstudio' },
        // generic description
        { vmid: 'description', name: 'description', content: 'IF!Live' },
        // og tags
        { vmid: 'ogtype', property: 'og:type', content: 'website' },
        { vmid: 'ogurl', property: 'og:url', content: 'https://' + this.$root.process.env.SERVICE_TARGET_DOMAIN + '/' + this.$route.fullPath },
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: 'IF!Live' },
        { vmid: 'ogdescription', property: 'og:description', content: 'IF!Live' },
        { vmid: 'ogimage', property: 'og:image', content: 'https://' + this.$root.process.env.SERVICE_TARGET_DOMAIN + shareImage },
        { vmid: 'ogimagetype', property: 'og:image:type', content: 'image/jpeg' },
        { vmid: 'ogimageheight', property: 'og:image:width', content: '1200' },
        { vmid: 'ogimagewidth', property: 'og:image:height', content: '630' }
      ]
    }
  }
}
</script>

<style lang="scss">
@font-face {
  font-family: 'Helvetica Neue';
  src: url('./assets/fonts/HelveticaNeue/HelveticaNeue-Thin.woff2') format('woff2'),
    url('./assets/fonts/HelveticaNeue/HelveticaNeue-Thin.woff') format('woff');
  font-weight: 100;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Helvetica Neue';
  src: url('./assets/fonts/HelveticaNeue/HelveticaNeue-Roman.woff2') format('woff2'),
    url('./assets/fonts/HelveticaNeue/HelveticaNeue-Roman.woff') format('woff');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Helvetica Neue';
  src: url('./assets/fonts/HelveticaNeue/HelveticaNeue-Light.woff2') format('woff2'),
    url('./assets/fonts/HelveticaNeue/HelveticaNeue-Light.woff') format('woff');
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Helvetica Neue';
  src: url('./assets/fonts/HelveticaNeue/HelveticaNeue-Bold.woff2') format('woff2'),
    url('./assets/fonts/HelveticaNeue/HelveticaNeue-Bold.woff') format('woff');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Turquoise';
  src: url('./assets/fonts/Turquoise/Turquoise-Regular.woff2') format('woff2'),
    url('./assets/fonts/Turquoise/Turquoise-Regular.woff') format('woff');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

.font-weight-semibold {
  font-weight: 600 !important;
}

.font-family-proxima {
  font-family: $real-body-font-family !important;
}

.v-application .v-main .font-family-heading {
  font-family: $heading-font-family !important;
}

.v-application .v-main .font-family-body {
  font-family: $body-font-family !important;
}

// .theme--dark.v-application {
//   transition: background-color 0.5s $ease-in-out-quad;
// }

// .theme--dark.v-application.dark-background {
//   background-color: #1b655d !important;
// }

// .theme--dark.v-application {
//   background: linear-gradient(0deg, map-deep-get($colors, 'teal', 'darken3') 0%, map-deep-get($colors, 'teal', 'base') 90%, map-deep-get($colors, 'teal', 'base') 100%) !important;
// }

.app {
  overflow-x: hidden;
}

.text-body-1,
.text-body-2 {
  a {
    color: map-get($blue, 'base') !important;
    transition: color 0.25s;

    &:hover {
      color: map-get($blue, 'darken-4') !important;
    }
  }
}

.global-bg {
  position: fixed;
  top: 64px;
  height: 70vh;
  width: 70vh;
  background-image: url('./assets/img/nuvola.png');
  background-position: 50% 50%;
  background-size: contain;
  z-index: 0;

  @include breakpoint('md-and-down') {
    top: 56px;
  }

  &.left {
    top: initial;
    left: 0;
    bottom: 50px;
    // transform: translateX(-72%);
    @include breakpoint('md-and-down') {
      bottom: 0;
      transform: translateX(-60%);
    }
  }

  &.right {
    right: 50px;
    background-image: url('./assets/img/uccello.png');
    // transform: scaleY(-1) scaleX(-1) translateX(-72%);

    @include breakpoint('md-and-down') {
      transform: translateX(50%);
    }
  }

}

.theme--dark.v-calendar-daily {
  border: 0 !important;

  .v-calendar-daily__day {
    border-right: 0 !important;
    border-bottom: 0 !important;
  }
}

.theme--dark.v-calendar-events .v-event-timed.white--text {
  border: 2px solid #3bc1b1 !important;
}

.v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.v-input input {
  text-align: center;
}

.v-card__title {
  word-break: normal !important;

}

.v-dialog {
  position: relative;
}

.dialog-with-close-button {
  overflow: visible !important;

  .v-sheet {
    max-height: 90vh;
    overflow-y: auto;
    border-radius: $border-radius-root;
  }
}

.text-truncated-three-lines {
  position: relative;
  display: inline-block;
  // height: 3.6em;
  max-height: 3em;

  // min-height: 3.7em;
  min-width: 100%;
  padding-right: 0.7em;
  // text-overflow: ellipsis;
  overflow: hidden;
  // text-align: justify;

  &::before {
    content: '...';
    position: absolute;
    bottom: 0;
    right: 0;
  }

  &::after {
    content: '';
    position: absolute;
    right: 0; /* note: not using bottom */
    width: 1em;
    background: map-deep-get($colors, 'blue', 'lighten-4');
  }

  &.text-body-2 {
    max-height: 4.8em;

    &::after {
      height: 1.5em;
    }
  }
}

// .v-label {
//   width: 100% !important;
//   max-width: 100% !important;
//   text-align: center;
// }

// .v-label--active {
//   width: auto !important;
// }

.app .v-btn .v-btn__content {
  // text-transform: none;
  font-family: $btn-font-family;
  max-width: 100%;
}

.v-chip-group--column {
  .v-slide-group__content {
    justify-content: center !important;
  }
}

// generic transition for when <transition> tag is used
.v-enter-active,
.v-leave-active {
  .no-prerenderer & {
    transition: opacity 0.25s $ease-in-out-quad;
  }
}

.v-enter,
.v-leave-to {
  opacity: 0;
}
// .primary {
//   background-color: #ccc;
// }

.app .theme--dark.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {  // stylelint-disable-line
  background-color: transparent !important;
}

.v-btn:not(.v-btn--icon):not(.no-underline).v-btn--active { // stylelint-disable-line

  .v-btn__content {
    text-decoration: underline;
  }

  &::before {
    opacity: 0 !important;
  }
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  box-shadow: 0 0 0 30px rgb(59, 193, 177) inset !important;
}

input:-webkit-autofill {
  -webkit-text-fill-color: #fff !important;
}

.plyr--full-ui input[type=range] {
  color: rgba(59, 193, 177, 0.9) !important;
}

.plyr__control:hover {
  background: rgba(59, 193, 177, 0.9) !important;
}

.plyr__control--overlaid {
  background: rgba(59, 193, 177, 0.9) !important;
  width: 100px !important;
  height: 100px !important;

  svg {
    width: 100% !important;
    height: 100% !important;
  }
}
</style>
